import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { PROCESS_AREAS } from '../../components/CallRecordingDetails/components/coachingRubric';
import { styleSheet } from './adherenceDrawer.styles';

type Tone = 'good' | 'missed' | 'neutral';

const STEP_LABELS: Partial<Record<Api.SalesCallStep, string>> = {
	...Object.fromEntries(PROCESS_AREAS.map(a => [a.step, a.label])),
	Other: 'Pacing / tone',
	Unknown: 'Unclassified',
};

const stepLabel = (step: Api.SalesCallStep) => STEP_LABELS[step] ?? step;

const CLASSIFICATION_META: Record<Api.CoachingClassification, { label: string; tone: Tone }> = {
	followed: { label: 'Followed', tone: 'good' },
	partially_followed: { label: 'Partly followed', tone: 'neutral' },
	not_followed: { label: 'Not followed', tone: 'missed' },
	not_applicable: { label: 'No chance to act', tone: 'neutral' },
};

const QUALITY_META: Record<Api.CoachingSuggestionQuality, { label: string; tone: Tone }> = {
	Sound: { label: 'Sound advice', tone: 'good' },
	Mistimed: { label: 'Mistimed', tone: 'neutral' },
	Irrelevant: { label: 'Irrelevant', tone: 'neutral' },
	Unknown: { label: 'Not judged', tone: 'neutral' },
};

const GAME_PLAN_META: Record<Api.GamePlanPitchAdherence, { label: string; sub: string; tone: Tone }> = {
	Delivered: { label: 'Delivered', sub: 'The rep used the pitch the game plan recommended', tone: 'good' },
	PartiallyDelivered: { label: 'Partly delivered', sub: 'Some of the recommended pitch landed', tone: 'neutral' },
	Ignored: { label: 'Not used', sub: 'The plan recommended a pitch and the rep did not deliver it', tone: 'missed' },
	NoOpportunity: {
		label: 'No opportunity',
		sub: 'The call ended before the recommended pitch was possible',
		tone: 'neutral',
	},
	NotApplicable: {
		label: 'Not applicable',
		sub: 'No game plan on this thread, or it recommended no pitch',
		tone: 'neutral',
	},
};

const toneStyle = (tone: Tone) =>
	tone === 'good' ? styleSheet.tagGood : tone === 'missed' ? styleSheet.tagMissed : styleSheet.tagNeutral;

// 0 is a real verdict (a skipped pitch, advice ignored); only a missing value means "not scored".
const formatPercent = (value?: number | null) => (value == null ? '—' : `${value}%`);

interface IProps {
	result: Api.ICoachingAdherenceResult;
}

/** Grades the AI coach rather than the rep, so it sits with the call scoring, not with adherence. */
export function CoachQualityTile({ result }: IProps) {
	return (
		<div className={css(styleSheet.scoreBox)}>
			<div className={css(styleSheet.scoreBoxLabel)}>Coach quality</div>
			<div className={css(styleSheet.scoreBoxValue)}>{formatPercent(result.suggestionQualityScore)}</div>
			<div className={css(styleSheet.scoreBoxSub)}>Share of suggestions that were sound advice</div>
		</div>
	);
}

export function AdherenceBreakdown({ result }: IProps) {
	const gamePlan = result.gamePlanPitch ?? null;
	const gamePlanMeta = GAME_PLAN_META[gamePlan?.adherence ?? 'NotApplicable'] ?? GAME_PLAN_META.NotApplicable;
	const suggestions = result.suggestionEvaluations ?? [];
	// Only these two verdicts mean the pitch was excluded rather than scored, so only they explain a missing
	// number. Any other verdict without a score has simply not been scored yet, and must not read as a zero.
	const pitchExcluded = gamePlan?.adherence === 'NoOpportunity' || gamePlan?.adherence === 'NotApplicable';
	const pitchAdherenceSub =
		result.pitchAdherenceScore != null
			? 'Whether the rep delivered the pitch their pre-call game plan recommended'
			: pitchExcluded
				? gamePlanMeta.sub
				: 'No pitch verdict scored on this call';

	return (
		<div className={css(styleSheet.adherenceColumns)}>
			<div className={css(styleSheet.adherenceColumn)}>
				<div className={css(styleSheet.scoreBox)}>
					<div className={css(styleSheet.scoreBoxLabel)}>Pitch adherence</div>
					<div className={css(styleSheet.scoreBoxValue, styleSheet.headlineValue)}>
						{formatPercent(result.pitchAdherenceScore)}
					</div>
					<div className={css(styleSheet.scoreBoxSub)}>{pitchAdherenceSub}</div>
				</div>
				{gamePlan && (
					<>
						<div className={css(styleSheet.effortBox)}>
							<div>
								<div className={css(styleSheet.effortLabel)}>Game plan pitch</div>
								{pitchAdherenceSub !== gamePlanMeta.sub && (
									<div className={css(styleSheet.effortSub)}>{gamePlanMeta.sub}</div>
								)}
							</div>
							<span className={css(styleSheet.tag, toneStyle(gamePlanMeta.tone))}>{gamePlanMeta.label}</span>
						</div>
						{(gamePlan.reasoning || gamePlan.repEvidence) && (
							<div className={css(styleSheet.whyBox)}>
								{gamePlan.reasoning && <div className={css(styleSheet.whyText)}>{gamePlan.reasoning}</div>}
								{gamePlan.repEvidence && (
									<div className={css(gamePlan.reasoning ? styleSheet.evidenceDivider : undefined)}>
										<div className={css(styleSheet.evidenceLabel)}>Rep said instead</div>
										<div className={css(styleSheet.evidenceText)}>{gamePlan.repEvidence}</div>
									</div>
								)}
							</div>
						)}
					</>
				)}
			</div>

			<div className={css(styleSheet.adherenceColumn)}>
				<div className={css(styleSheet.scoreBox)}>
					<div className={css(styleSheet.scoreBoxLabel)}>Live suggestion adherence</div>
					<div className={css(styleSheet.scoreBoxValue, styleSheet.headlineValue)}>
						{formatPercent(result.suggestionAdherenceScore)}
					</div>
					<div className={css(styleSheet.scoreBoxSub)}>
						Share of <b>sound</b> in-call advice the rep acted on
					</div>
				</div>
				<div className={css(styleSheet.effortBox)} title='The only cell that becomes coaching'>
					<div className={css(styleSheet.effortLabel)}>Sound advice missed</div>
					<div className={css(styleSheet.effortValue)}>{result.missedSoundSuggestions ?? '—'}</div>
				</div>

				{/* Not judged is not the same as nothing to see, so say which one it is. */}
				{result.suggestionQualityScore == null && suggestions.length > 0 && (
					<div className={css(styleSheet.note, styleSheet.noteWarning)}>
						These suggestions were never quality-judged, so they cannot count toward adherence. Calls coached before the
						quality check shipped all read this way.
					</div>
				)}

				{suggestions.length > 0 && (
					<div className={css(styleSheet.rowList)}>
						{suggestions.map((s, i) => {
							const cls = CLASSIFICATION_META[s.classification] ?? {
								label: s.classification,
								tone: 'neutral' as const,
							};
							const qual = QUALITY_META[s.quality ?? 'Unknown'] ?? QUALITY_META.Unknown;
							const isFinding = s.quality === 'Sound' && s.classification === 'not_followed';
							return (
								<div
									key={`${s.step}-${i}`}
									className={css(styleSheet.suggestionRow, isFinding && styleSheet.suggestionRowMissed)}
								>
									<div className={css(styleSheet.suggestionHead)}>
										<span className={css(styleSheet.suggestionStep)}>{stepLabel(s.step)}</span>
										<span className={css(styleSheet.tag, toneStyle(qual.tone))}>{qual.label}</span>
										<span className={css(styleSheet.tag, toneStyle(cls.tone))}>{cls.label}</span>
									</div>
									{s.reasoning && <div className={css(styleSheet.suggestionReasoning)}>{s.reasoning}</div>}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
